import React from 'react'
import {useThemeMode, systemMode} from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'

type Props = {
  // New: pre-computed values (Admin Dashboard)
  createdToday?: number
  closedToday?: number
  createdYesterday?: number
  closedYesterday?: number
  // Old: raw data arrays (Manager/Supervisor/Finance)
  activeJobs?: any[]
  closedJobs?: any[]
  className?: string
  loading?: boolean
}

export const JobsActivityCard: React.FC<Props> = ({
  createdToday: createdTodayProp,
  closedToday: closedTodayProp,
  createdYesterday: createdYesterdayProp,
  closedYesterday: closedYesterdayProp,
  activeJobs,
  closedJobs,
  className,
  loading
}) => {
  // Compute from raw data if pre-computed values not provided
  const isPreComputed = createdTodayProp !== undefined;
  let createdToday = 0, closedToday = 0, createdYesterday = 0, closedYesterday = 0;

  if (isPreComputed) {
    createdToday = createdTodayProp || 0;
    closedToday = closedTodayProp || 0;
    createdYesterday = createdYesterdayProp || 0;
    closedYesterday = closedYesterdayProp || 0;
  } else {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    const allJobs = [...(activeJobs || []), ...(closedJobs || [])];

    for (const j of allJobs) {
      const created = new Date(j.createdOn);
      if (created >= startOfToday) createdToday++;
      else if (created >= startOfYesterday) createdYesterday++;
    }
    for (const j of (closedJobs || [])) {
      const ended = j.endDate ? new Date(j.endDate) : null;
      if (ended && ended >= startOfToday) closedToday++;
      else if (ended && ended >= startOfYesterday) closedYesterday++;
    }
  }
  const {mode} = useThemeMode()
  const isDarkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark')

  const StatBox = ({icon, value, label, color}: {icon: string, value: number, label: string, color: string}) => (
    <div
      className="d-flex align-items-center rounded p-3 flex-grow-1"
      style={{
        background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#F9F9F9',
        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #F0F0F0'
      }}
    >
      <div
        className="symbol symbol-40px me-3"
        style={{
          background: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : `${color}20`
        }}
      >
        <div className="symbol-label" style={{background: 'transparent'}}>
          <i className={`${icon} fs-3`} style={{color}}></i>
        </div>
      </div>
      <div className="d-flex flex-column">
        <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'} fw-bold fs-4 lh-1`}>
          {value}
        </span>
        <span className={`${isDarkMode ? 'text-white' : 'text-gray-500'} opacity-60 fs-8 fw-semibold mt-1`}>
          {label}
        </span>
      </div>
    </div>
  )

  return (
    <div
      className={`card card-flush ${className}`}
      style={{
        background: isDarkMode
          ? 'linear-gradient(112.14deg, #1B1B29 0%, #161621 100%)'
          : '#ffffff',
        boxShadow: isDarkMode ? 'none' : '0px 0px 20px 0px rgba(76, 87, 125, 0.02)'
      }}
    >
      <div className="card-body d-flex flex-column justify-content-between p-6">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-5">
          <div className="d-flex align-items-center">
            <div
              className="symbol symbol-45px symbol-circle me-3"
              style={{
                background: isDarkMode
                  ? 'rgba(255, 255, 255, 0.2)'
                  : 'rgba(0, 158, 247, 0.1)'
              }}
            >
              <div className="symbol-label" style={{ background: 'transparent' }}>
                <i className={`bi bi-clipboard-data-fill fs-2 ${isDarkMode ? 'text-white' : 'text-primary'}`}></i>
              </div>
            </div>
            <div className="d-flex flex-column">
              <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'} fw-bold fs-4`}>
                Jobs Activity
              </span>
              <span className={`${isDarkMode ? 'text-white' : 'text-gray-500'} opacity-75 fw-semibold fs-7`}>
                Creation & Completion
              </span>
            </div>
          </div>
          {loading && (
            <span className={`spinner-border spinner-border-sm ${isDarkMode ? 'text-white' : 'text-primary'} opacity-50`}></span>
          )}
        </div>

        {/* Today Stats */}
        <div className="mb-4">
          <div className={`${isDarkMode ? 'text-white' : 'text-gray-700'} fw-bold fs-6 mb-3 opacity-75`}>
            <i className="bi bi-calendar-day me-2"></i>Today
          </div>
          <div className="d-flex gap-3">
            <StatBox
              icon="bi bi-plus-circle-fill"
              value={createdToday}
              label="Created"
              color="#009ef7"
            />
            <StatBox
              icon="bi bi-check-circle-fill"
              value={closedToday}
              label="Closed"
              color="#50cd89"
            />
          </div>
        </div>

        {/* Yesterday Stats */}
        <div>
          <div className={`${isDarkMode ? 'text-white' : 'text-gray-700'} fw-bold fs-6 mb-3 opacity-75`}>
            <i className="bi bi-calendar-minus me-2"></i>Yesterday
          </div>
          <div className="d-flex gap-3">
            <StatBox
              icon="bi bi-plus-circle"
              value={createdYesterday}
              label="Created"
              color="#7239ea"
            />
            <StatBox
              icon="bi bi-check-circle"
              value={closedYesterday}
              label="Closed"
              color="#ffc700"
            />
          </div>
        </div>
      </div>
    </div>
  )
}
